import { Theme } from "@mui/material/styles";
import { TextFieldProps } from "@mui/material/TextField";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { ERROR_COLOR } from "ds_legacy/materials/colors";
import { border, dp, padding } from "ds_legacy/materials/metrics";
import { SelectType } from "./types";

export const useStyles = ({
  fontSize,
  hasError,
  isSearchable,
}: {
  fontSize: string;
  hasError: boolean;
  isSearchable: boolean | undefined;
}) =>
  makeStyles((theme: Theme) =>
    createStyles({
      divider: {
        height: theme.spacing(2),
      },
      dropdownIndicatorContainer: {
        cursor: "pointer",
      },
      input: {
        alignItems: "center",
        cursor: isSearchable ? "text" : "pointer",
        display: "flex",
        height: "auto",
        justifyContent: "space-between",
        padding: 0,
      },
      inputError: {
        borderBottom: border({ color: ERROR_COLOR, width: 2 }),
      },
      menuItem: {
        "&.Mui-selected": {
          outline: `${dp(1)} solid ${theme.palette.primary.dark}`,
        },
        display: "block !important",
        fontSize: `${fontSize} !important`,
        maxWidth: "100%",
        outlineOffset: dp(-2),
        overflow: "hidden",
        padding: padding(1, 2),
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      noOptionsMessage: {
        padding: theme.spacing(1, 2),
      },
      paper: {
        left: 0,
        marginTop: theme.spacing(1),
        overflow: "initial",
        position: "relative",
        right: 0,
        zIndex: 10000,
      },
      placeholder: {
        color: hasError ? ERROR_COLOR : undefined,
        fontSize,
      },
      root: {
        flexGrow: 1,
        marginTop: 0,
      },
      singleValue: {
        color: hasError ? ERROR_COLOR : undefined,
        fontSize,
        maxWidth: `calc(100% - ${dp(40)})`,
        overflow: "hidden",
        position: "absolute",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      valueContainerMulti: {
        alignItems: "center",
        display: "inline-flex",
        flexWrap: "wrap",
        padding: padding(0),
        width: `calc(100% - ${dp(44)})`,
      },
      valueContainerMultiEllipsis: {
        display: "inline-block",
        fontSize,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      valueContainerMultiItems: {
        "&> div": {
          margin: dp(2),
        },
        "&> div:last-child": {
          flexGrow: 0,
          minHeight: dp(32),
        },
        alignItems: "center",
        display: "flex",

        flexWrap: "wrap",

        maxWidth: "100%",
      },
      valueContainerSingle: {
        alignItems: "center",
        cursor: "pointer",
        display: "flex",
        fontSize,
        maxWidth: `calc(100% - ${dp(40)})`,
        padding: padding(0),
      },
      valueContainerSingleItem: {
        alignItems: "center",
        display: "flex",
        flexWrap: "wrap",
      },
    }),
  );

export function isValueEmpty(selectValue: any, multiple: boolean) {
  if (!selectValue) return true;

  // is multi select
  if (multiple) {
    return (
      (Array.isArray(selectValue) && selectValue.length === 0) ||
      (Array.isArray(selectValue.value) && selectValue.value.length === 0)
    );
  }

  // is single select
  if (selectValue instanceof Object && !selectValue.label) {
    return true;
  }
  return typeof selectValue === "string" && selectValue.length === 0;
}

export function getTextFieldStyles({
  fontSize,
  hasError,
}: {
  fontSize: string;
  hasError?: boolean;
}) {
  let textFieldStyles = {
    fontSize,
    padding: padding(0, 0, 1),
    transform: `translate(0, ${dp(4)}) scale(0.8)`,
  };
  if (hasError) {
    textFieldStyles = Object.assign(textFieldStyles, { color: ERROR_COLOR });
  }
  return textFieldStyles;
}

export function getTextFieldProps({
  customNoResult,
  elementName,
  label,
  onBlur,
  onFocus,
  onSearch,
  placeholder,
  required,
  setSearchInputValue,
  testId,
  textFieldStyles,
}: Partial<SelectType> & {
  empty: boolean;
  setSearchInputValue: React.Dispatch<React.SetStateAction<string>>;
  textFieldStyles: ReturnType<typeof getTextFieldStyles>;
}) {
  const handleFocus: TextFieldProps["onFocus"] = (e) => {
    onFocus?.(e);
  };
  const handleBlur: TextFieldProps["onBlur"] = (e) => {
    onBlur?.(e);
  };
  const handleChange: TextFieldProps["onChange"] = (e) => {
    if (customNoResult) {
      setSearchInputValue(e.target?.value);
    }
    onSearch?.(e);
  };

  let formattedLabel = "";
  if (label) {
    formattedLabel = required ? `${label} *` : label;
  }

  return {
    "data-testid": testId ?? elementName,
    InputLabelProps: {
      style: textFieldStyles,
    },
    label: formattedLabel,
    onBlur: handleBlur,
    onChange: handleChange,
    onFocus: handleFocus,
    placeholder,
  };
}
